import { useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import type SwiperCore from 'swiper'
import { Autoplay } from 'swiper/modules'
import { trackEvent } from '~/utils/tracking'
import { FlexContainer } from '~/components/Grid'
import { createOptimizedContentfulImage } from '~/libs/contentful/helpers'
import { TypeHomePageSliderItemSkeleton, TypeHomePageSliderSkeleton } from '~/types/contentful'
import { Asset, AssetFile, Entry, EntrySkeletonType } from 'contentful'

type MainSliderProps = {
  section: Entry<TypeHomePageSliderSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>
}

export const MainSlider = ({ section }: MainSliderProps) => {
  const progress = useRef<HTMLDivElement | null>(null)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const [swiper, setSwiper] = useState<SwiperCore | null>(null)
  const data = section.fields

  let interval: NodeJS.Timeout

  const move = () => {
    const elem = progress.current

    const autoplayTime = (data.autoplayDelay as number) / 100
    let width = 1

    clearInterval(interval)
    interval = setInterval(frame, autoplayTime)

    function frame() {
      if (width >= 100) {
        clearInterval(interval)
      } else {
        width++
        elem?.style?.setProperty('width', `${width}%`)
      }
    }
  }

  const handleOnSlideClick = (index: number, groupName: string) => {
    if (!swiper) return
    swiper?.slideTo(index + 1)

    trackEvent('homepage_toggle_features', {
      block: data.blockId as string,
      homepage_solution_title: groupName,
    })
  }

  const handleFeatureServiceClick = (serviceName: string, target: string) => {
    trackEvent('homepage_click_features_service', {
      block: data.blockId as string,
      homepage_services: serviceName,
      target,
    })
  }

  return (
    <section
      className="my-25 -m-8 max-xl:px-11 max-lg:my-0 max-lg:-mx-6 max-md:pt-6 max-md:px-0 max-md:pb-14 max-md:my-0 max-md:-mx-5"
      data-block-id={section.fields.blockId}
    >
      <FlexContainer className="flex-col overflow-hidden rounded-[48px] bg-[#f4f6fa]">
        <div
          className="bg-neutral-20 transition-[width] duration-100 ease-linear h-1"
          ref={progress}
        />

        <div className="flex gap-8 max-md:justify-center max-md:gap-5 max-xl:pt-7 max-xl:pb-0 max-xl:px-9 pt-9 pb-0 pr-9 pl-15 w-full text-s">
          {(
            data.slideList as Entry<
              TypeHomePageSliderItemSkeleton,
              'WITHOUT_UNRESOLVABLE_LINKS',
              string
            >[]
          ).map((slide, index: number) => (
            <p
              className="w-fit cursor-pointer"
              style={{ color: index === activeIndex ? '#0F141E' : '#9FA6BB' }}
              key={`${index} ${slide.fields.groupName}`}
              onClick={() => handleOnSlideClick(index, slide.fields.groupName as string)}
            >
              <span className="max-md:hidden">{slide.fields.groupName}</span>
              <b className="hidden max-md:block max-md:text-xs max-md:font-normal">
                {slide.fields.shortName}
              </b>
            </p>
          ))}
        </div>

        <div
          className="relative pt-3 overflow-hidden lg:pt-8 [&_.swiper-slide]:flex-col [&_.swiper-slide]:flex-shrink-0 w-full"
          style={{ userSelect: 'none' }}
        >
          <Swiper
            slidesPerView={1}
            speed={600}
            modules={[Autoplay]}
            autoplay={{
              delay: data.autoplayDelay,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            loop
            onSwiper={(s: SwiperCore) => {
              setSwiper(s)
            }}
            onProgress={() => move()}
            onSlideChange={(e) => {
              setActiveIndex(e.realIndex)
            }}
          >
            {(
              data.slideList as Entry<
                TypeHomePageSliderItemSkeleton,
                'WITHOUT_UNRESOLVABLE_LINKS',
                string
              >[]
            ).map((item) => (
              <SwiperSlide key={item.sys.id}>
                <FlexContainer
                  className="p-[0_38px_32.5px_60px] max-xl:p-[0_25px_32.5px_25px] max-md:flex-col max-md:items-center max-md:pb-8 max-md:rounded-3xl lg:!mx-0"
                  justify={'between'}
                  flexWrap={'nowrap'}
                >
                  <div className="pt-19 max-w-[418px] max-xl:pt-14 max-xl:max-w-[350px] max-md:pt-4 max-md:max-w-full">
                    <h2 className="mb-6 max-lg:mb-5 max-md:text-center max-md:mb-4 text-xl">
                      {item.fields.title}
                    </h2>
                    <p className="mb-15 max-lg:mb-21 max-md:mb-4 max-md:text-center text-s">
                      {item.fields.description}
                    </p>

                    <div className="max-md:block max-md:mb-7 hidden items-end min-h-[300px] min-w-[300px] max-w-[500px] max-h-[500px] max-md:min-h-[220px] max-md:max-h-[220px] max-md:h-[220px]">
                      <img
                        className="block w-full h-auto max-md:h-full max-md:object-contain"
                        src={createOptimizedContentfulImage({
                          file: (item.fields.image as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>)
                            ?.fields.file as AssetFile,
                        })}
                        alt={
                          (item.fields.image as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>)?.fields
                            .title
                        }
                        loading="lazy"
                      />
                    </div>
                    <FlexContainer className="gap-4 max-md:grid max-md:grid-cols-2 max-md:[&:nth-child(2)]:hidden">
                      {item.fields.links?.map((link) => (
                        <a
                          className="max-lg:basis-full text-blue-50 basis-[44%] text-xs"
                          href={
                            (link as Entry<EntrySkeletonType, 'WITHOUT_UNRESOLVABLE_LINKS', string>)
                              .fields.link as string
                          }
                          key={
                            (link as Entry<EntrySkeletonType, 'WITHOUT_UNRESOLVABLE_LINKS', string>)
                              .fields.title as string
                          }
                          onClick={() =>
                            handleFeatureServiceClick(
                              (
                                link as Entry<
                                  EntrySkeletonType,
                                  'WITHOUT_UNRESOLVABLE_LINKS',
                                  string
                                >
                              ).fields.title as string,
                              (
                                link as Entry<
                                  EntrySkeletonType,
                                  'WITHOUT_UNRESOLVABLE_LINKS',
                                  string
                                >
                              ).fields.link as string,
                            )
                          }
                        >
                          {
                            (link as Entry<EntrySkeletonType, 'WITHOUT_UNRESOLVABLE_LINKS', string>)
                              .fields.title as string
                          }
                        </a>
                      ))}
                    </FlexContainer>
                  </div>
                  <div className="max-md:[&_img]:object-contain flex items-end min-h-[300px] min-w-[300px] max-w-[500px] max-h-[500px] max-md:min-h-[220px] max-md:max-h-[220px] max-md:h-[220px] max-md:hidden">
                    <img
                      className="block w-full h-auto max-md:h-full"
                      src={createOptimizedContentfulImage({
                        file: (item.fields.image as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>)
                          ?.fields.file as AssetFile,
                      })}
                      alt={
                        (item.fields.image as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>)?.fields
                          .title
                      }
                      loading="lazy"
                    />
                  </div>
                </FlexContainer>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </FlexContainer>
    </section>
  )
}
